import moment from 'utils/app-moment';
import { ticketConstants } from 'store/constants';
import { LoadTicketMode } from 'store/constants/ticket.constants';
import { AssigneesResI } from 'store/models/Assignees';
import {
  ApiAttachment,
  FormattedMainFile,
} from 'store/models/MainFiles';
import {
  TicketApiData,
  TicketDataFromMainPage,
  Ticket as TicketModel,
  ToAutosaveApiI,
} from 'store/models/Ticket';
import {
  getSameValueObjectWithKeys,
  replaceAllValuesOfObject,
} from 'utils/object-utils';

const initialState: {
  isLoading: boolean;
  data?: TicketApiData; // | TicketDataFromMainPage
  deletingPartIds: string[];
  formTicket: TicketDataFromMainPage | null;
  recentTicketId?: null | string; // recently opened
  autoMarkIsSplitError: string;
  attachments: ApiAttachment[];
  checkingBookingInfo: boolean;
  triggerPreprocessStatus: null | 'REQUESTING' | 'SUCCESS' | 'ERROR';
  triggerPreprocessError: null | string;
  isLoadingPreviewBookings: object;
  previewParamsSubmitted: undefined | object;
  previewResult: object;
  onLoadFormToAutosaveApiData: undefined | ToAutosaveApiI;
  onLoadIsUnassigned: undefined | boolean;
} = {
  isLoading: false,
  deletingPartIds: [],
  formTicket: null,
  recentTicketId: undefined,
  autoMarkIsSplitError: '',
  attachments: [],
  checkingBookingInfo: false,
  triggerPreprocessStatus: null,
  triggerPreprocessError: null,
  isLoadingPreviewBookings: {},
  previewParamsSubmitted: undefined,
  previewResult: {},
  onLoadFormToAutosaveApiData: undefined,
  onLoadIsUnassigned: undefined,
};
export const ticket = (
  state = initialState,
  action: {
    type: string;
    payload: {
      attachment: FormattedMainFile;
      partId?: string;
      status?: null | 'REQUESTING';
      deletingPartIds?: string[];
      data?: TicketApiData;
      successCount?: number;
      error?: string;
      failedPartIds?: string[];
      params?: any; // only any for now
      mode?: LoadTicketMode;
      si?: boolean;
      key?: string;
      value?: any;
      formTicket?: TicketDataFromMainPage;
      assignee?: AssigneesResI;
      senderId?: string;
      updatedDetail?: any;
      autoSaveData?: any; // any for now
      mainBookingHasErrors: null | object;
      attachments?: ApiAttachment[];
      isRecycled?: boolean;
      bookingNos?: string[];
      previewParamsSubmitted?: object;
      previewResult?: object; // TODO: double check the type. depends on what submitBooking returns
      onLoadFormToAutosaveApiData?: ToAutosaveApiI;
      recycledAttachmentPartIds?: string[];
      onLoadIsUnassigned?: boolean;
    };
  },
) => {
  const { data, deletingPartIds } = state;
  let attachments;
  switch (action.type) {
    case ticketConstants.SET_AUTO_MARK_IS_SPLIT_ERROR_TXT:
      if (action.payload.error === undefined)
        throw Error(`payload.error is missing`);
      return {
        ...state,
        autoMarkIsSplitError: action.payload.error,
      };
    case ticketConstants.MERGE_TO_TICKET:
      if (!action.payload.updatedDetail)
        throw Error(`payload.updatedDetail is missing`);
      const { autoSave, ...others } = action.payload.updatedDetail;
      let result = {
        ...state,
        data: {
          ...state.data,
          ...others,
        },
      };
      if (autoSave) {
        if (!result.data.autoSave) result.data.autoSave = {};
        result.data.autoSave = {
          ...result.data.autoSave,
          data: {
            ...(result.data.autoSave?.data || {}),
            ...autoSave,
          },
        };
      }
      return result;
    case ticketConstants.REMOVE_TICKET_ASSIGNEE:
      return {
        ...state,
        data: {
          ...state.data,
          assignee: null,
        },
      };
    case ticketConstants.UPDATE_TICKET_ASSIGNEE:
      if (!action.payload.assignee)
        throw Error(`payload.assignee is missing`);
      return {
        ...state,
        data: {
          ...state.data,
          assignee: action.payload.assignee,
        },
      };
    case ticketConstants.RESET_TICKET:
      return { ...initialState, recentTicketId: state.data?.id };
    case ticketConstants.UPDATE_SI:
      if (action.payload.si === undefined)
        throw Error(`missing action.payload.si`);
      return {
        ...state,
        data: {
          ...data,
          si: action.payload.si,
        } as TicketApiData,
      };
    case ticketConstants.LOAD_TICKET_REQUEST:
      // if init erase data
      if (action.payload.mode === 'INIT') {
        return {
          ...initialState,
          isLoading: true,
        };
      }
      return {
        ...state,
        isLoading: action.payload.mode
          ? !['AFTER_UPLOAD_FILE', 'INTERVAL'].includes(
              action.payload.mode,
            )
          : false,
      };
    case ticketConstants.LOAD_TICKET_SUCCESS:
      return {
        ...state,
        isLoading: false,
        data: action.payload.data, // TODO: consider change the key name stored
        attachments: TicketModel.apiToStoreAttachments(
          action.payload.data,
        ),
        successCount: action.payload.successCount, // TODO: check if need this
      };
    case ticketConstants.CACHE_ON_LOAD_FORM_TO_AUTOSAVE_API_DATA:
      return {
        ...state,
        onLoadFormToAutosaveApiData:
          action.payload.onLoadFormToAutosaveApiData,
      };
    case ticketConstants.CACHE_ON_LOAD_IS_UNASSIGNED:
      return {
        ...state,
        onLoadIsUnassigned: action.payload.onLoadIsUnassigned,
      };

    case ticketConstants.LOAD_TICKET_ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload.error, // TODO: consider change the key name stored
      };
    case ticketConstants.UPDATE_ATTACHMENTS:
      return {
        ...state,
        attachments: TicketModel.apiAttachmentsToStoreAttachments(
          action.payload.attachments,
          action.payload.recycledAttachmentPartIds,
        ),
        data: {
          ...state.data,
          attachments: action.payload.attachments ?? [],
        },
      };
    case ticketConstants.DELETE_FILES_REQUEST:
    case ticketConstants.UPDATE_FILES_RECYCLE_STATUS_REQUEST:
      if (!action.payload.deletingPartIds) {
        return state;
      } else {
        return {
          ...state,
          deletingPartIds: action.payload.deletingPartIds,
          attachments: state.attachments.map((att) =>
            (action.payload.deletingPartIds as string[]).includes(
              att.partId,
            )
              ? { ...att, status: 'REQUESTING' }
              : att,
          ),
        };
      }
    case ticketConstants.DELETE_FILES_SUCCESS:
      if (!deletingPartIds || !deletingPartIds.length) {
        return state;
      } else {
        const successPartIds = deletingPartIds.filter(
          (id) =>
            !(action.payload.failedPartIds as string[]).includes(id),
        );
        return {
          ...state,
          deletingPartIds: [],
          attachments: state.attachments
            .filter((att) => !successPartIds.includes(att.partId))
            .map((att) =>
              (action.payload.failedPartIds as string[]).includes(
                att.partId,
              )
                ? {
                    ...att,
                    status: 'ERROR',
                    error: 'failed to delete',
                  }
                : att,
            ),
        };
      }

    case ticketConstants.DELETE_FILES_ERROR:
    case ticketConstants.UPDATE_FILES_RECYCLE_STATUS_ERROR:
      if (!deletingPartIds || !deletingPartIds.length) {
        return state;
      } else {
        return {
          ...state,
          deletingPartIds: [],
          attachments: state.attachments.map((att) =>
            deletingPartIds.includes(att.partId)
              ? {
                  ...att,
                  status: 'ERROR',
                  error: action.payload.error,
                }
              : att,
          ),
        };
      }
    case ticketConstants.UPDATE_FILES_RECYCLE_STATUS_SUCCESS:
      // TODO: consider chaning to updateStatusPartIds
      if (!deletingPartIds || !deletingPartIds.length) {
        return state;
      } else {
        const successPartIds = deletingPartIds.filter(
          (id) =>
            !(action.payload.failedPartIds as string[]).includes(id),
        );
        const newAttachments = state.attachments.map((att) => {
          return (action.payload.failedPartIds as string[]).includes(
            att.partId,
          )
            ? {
                ...att,
                status: 'ERROR',
                error: 'failed to delete',
              }
            : {
                ...att,
                isRecycled: successPartIds.includes(att.partId)
                  ? action.payload.isRecycled
                  : att.isRecycled, // not in successPartIds use the old value
                status: 'Done',
              };
        });
        return {
          ...state,
          deletingPartIds: [],
          attachments: newAttachments,
        };
      }
    case ticketConstants.SUBMIT_BOOKING_REQUEST:
      // TODO: might move all these to submit-booking.reducer.ts
      return {
        ...state,
        submitBookingParams: action.payload.params,
        isLoadingSubmitBooking: true,
      };
    case ticketConstants.SUBMIT_BOOKING_SUCCESS:
    case ticketConstants.SUBMIT_BOOKING_STOP_LOADING:
      return {
        ...state,
        isLoadingSubmitBooking: false,
      };
    case ticketConstants.SUBMIT_BOOKING_ERROR:
      return {
        ...state,
        isLoadingSubmitBooking: false,
        loadSubmitBookingError: action.payload.error, // for preview // TODO: review this
      };
    case ticketConstants.PREVIEW_BOOKING_REQUEST:
      if (!action.payload.bookingNos)
        throw TypeError('bookingNos is missing');
      const nonStringI = action.payload.bookingNos.findIndex(
        (bn) => typeof bn !== 'string',
      );
      if (nonStringI > -1) {
        throw TypeError(
          `bookingNo should be string. Found non-string at index ${nonStringI}`,
        );
      }
      return {
        ...state,
        isLoadingPreviewBookings: {
          ...state.isLoadingPreviewBookings,
          ...getSameValueObjectWithKeys(
            action.payload.bookingNos,
            true,
          ),
        },
      };
    case ticketConstants.PREVIEW_BOOKING_SUCCESS:
      if (!action.payload.previewParamsSubmitted) {
        throw TypeError('missing previewParamsSubmitted');
      }
      if (!action.payload.previewResult) {
        throw TypeError('missing previewResult');
      }
      console.warn(
        'TODO: double check reducer. might have to do deep merge.',
      );
      return {
        ...state,
        previewParamsSubmitted: action.payload.previewParamsSubmitted, // TODO: consider not storing this if can tell by the previewResult
        previewResult: action.payload.previewResult, // TODO: might have to do a deep merge
        isLoadingPreviewBookings: replaceAllValuesOfObject(
          state.isLoadingPreviewBookings,
          false,
        ),
      };
    case ticketConstants.PREVIEW_BOOKING_ERROR:
      return {
        ...state,
        isLoadingPreviewBookings: replaceAllValuesOfObject(
          state.isLoadingPreviewBookings,
          false,
        ),
      };
    case ticketConstants.SAVE_TICKET_REQUEST:
      return {
        ...state,
        isAutosaving: true,
      };
    case ticketConstants.SAVE_TICKET_SUCCESS:
      if (!action.payload.autoSaveData)
        throw Error(`payload.autoSave is missing`);
      return {
        ...state,
        isAutosaving: false,
        autoSaveStatus: 'SUCCESS',
        data: state.data && {
          ...state.data,
          autoSave: state.data?.autoSave && {
            ...state.data?.autoSave,
            data: {
              ...(state.data?.autoSave?.data || {}),
              ...action.payload.autoSaveData,
            },
            updateDate: moment().valueOf(),
          },
        },
      };
    case ticketConstants.SAVE_TICKET_ERROR:
      return {
        ...state,
        isAutosaving: false,
        autoSaveStatus: 'FAILED', // should this be failed or error?
      };
    case ticketConstants.UPDATE_FORM_TICKET_AT_KEY:
      console.debug(`UPDATE_FORM_TICKET_AT_KEY`, state.formTicket);
      return {
        ...state,
        formTicket: {
          ...state.formTicket,
          [action.payload.key as string]: action.payload.value,
        },
      };
    case ticketConstants.SET_FORM_TICKET:
      return {
        ...state,
        formTicket: action.payload.formTicket,
      };
    case ticketConstants.UPDATE_TICKET_ATT:
      if (!action.payload.attachment)
        throw Error(`need action.payload.attachment`);
      let updated = data?.attachments || [];
      const isInOld =
        updated.findIndex(
          (att) => att.partId === action.payload.attachment.partId,
        ) > -1;
      if (isInOld) {
        updated = updated.map((att) =>
          att.partId !== action.payload.attachment.partId
            ? att
            : action.payload.attachment,
        );
      } else {
        updated = updated.concat(action.payload.attachment);
      }
      attachments = updated;
      return {
        ...state,
        attachments,
      };
    case ticketConstants.UPDATE_TICKET_ATTACHMENTS:
      return {
        ...state,
        attachments: action.payload.attachments,
      };
    case ticketConstants.UPDATE_TICKET_FILE_STATUS:
      if (!data) throw Error(`no ticket found from store`);
      attachments = (data.attachments || []).map((att) =>
        att.partId !== action.payload.partId
          ? att
          : { ...att, status: action.payload.status },
      );
      return {
        ...state,
        attachments,
      };
    case ticketConstants.SET_CHECKING_BOOKING_INFO:
      if (action.payload.value === undefined)
        throw Error(`need action.payload.value`);
      return {
        ...state,
        checkingBookingInfo: action.payload.value,
      };
    case ticketConstants.TRIGGER_PREPROCESS_REQUEST:
      return {
        ...state,
        isLoading: true,
        triggerPreprocessStatus: 'REQUESTING',
        triggerPreProcessError: null,
      };
    case ticketConstants.TRIGGER_PREPROCESS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        triggerPreprocessStatus: 'SUCCESS',
        triggerPreProcessError: null,
      };
    case ticketConstants.TRIGGER_PREPROCESS_ERROR:
      return {
        ...state,
        isLoading: false,
        triggerPreprocessStatus: 'ERROR',
        triggerPreProcessError: null,
      };
    default:
      return state;
  }
};
